import { TFunction } from 'i18next';
import { LONG_FULL_DATE_FORMAT, ENGLISH, GERMAN, DOORMAN_PREFIX_DE, DOORMAN_PREFIX_EN, HEADER_TO_FILTER, DESCRIPTION, MAIN_CR_FIELD, SUB_CR_PATH, LAST_NAME, OVERVIEW_COLUMNS, VERSION_DATE_RAW, CREATED_RAW, CREATION_DATE_RAW, VEHICLE_STATUS_COMMON_COUNT_COLUMNS } from '@/helpers/constants';
import { combinedMenus, opensearchFieldMapping } from '@/helpers/opensearch-utils';
import { getAffectedModelSeries, getLocationInternalTranslation, getFullFormattedDate, parseObjectNegativeValuesToBlank } from '@/helpers/utils';
import { ColumnExtensionOverview, FormatterData, FilterKeysToTranslate, TableTranslations, VisibilityColumnsOverview } from '@/types';

export const parseDateToMidnight = (value: unknown): Date | null => {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;

    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const valueString = String(value);

  const parsedFromIso = new Date(valueString.replace(' - ', 'T'));

  if (!Number.isNaN(parsedFromIso.getTime())) {
    return new Date(parsedFromIso.getFullYear(), parsedFromIso.getMonth(), parsedFromIso.getDate());
  }

  const datePart = valueString.slice(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);

  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;

  return new Date(year, month - 1, day);
};

export const formatDateToYMD = (value: unknown): string => {
  const date = parseDateToMidnight(value);

  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const DATE_FILTER_PARAMS: Record<string, unknown> = {
  filterOptions: ['inRange'],
  inRangeInclusive: true,
  comparator: (filterDate: Date, cellValue: unknown) => {
    const cellDate = parseDateToMidnight(cellValue);

    if (!cellDate) return 1;
    if (cellDate < filterDate) return -1;
    if (cellDate > filterDate) return 1;

    return 0;
  },
};

type TableColumnDef = {
  name: string;
  title: string;
  getCellValue?: (row: Record<string, unknown>, columnName: string) => unknown;
};

type TableColumnResizingExtension = {
  columnName: string;
  minWidth: number;
  maxWidth: number;
};

type TableColumnsData = {
  columns: TableColumnDef[];
  columnsNotTree?: TableColumnDef[];
  extensions: ColumnExtensionOverview[];
  columnResizingExtensions?: TableColumnResizingExtension[];
};

export const tableColumns = (t, isSupplier, isCampaignOverview, isSubCROverview, formatterData?: FormatterData<TableTranslations | undefined>) => {
  const info = {
    users: {
      columns: [
        { name: LAST_NAME, title: t('dialog.lastName') },
        { name: 'firstName', title: t('dialog.firstName') },
        { name: 'department', title: t('dialog.department') },
        { name: 'userCode', title: t('dialog.userId') },
      ],
      extensions: [
        { columnName: LAST_NAME, width: 160 },
        { columnName: 'firstName', width: 160 },
        { columnName: 'department', width: 160 },
        { columnName: 'userCode', width: 160 },
      ],
    },
    suppliers: {
      columns: [
        { name: 'supplierId', title: t('dialog.supplierNumber') },
        { name: 'supplierName', title: t('dialog.supplierName') },
      ],
      extensions: [
        { columnName: 'supplierId', width: 200 },
        { columnName: 'supplierName', width: 440 },
      ],
    },
    campaignOverview: {
      columns: [
        { name: 'title', title: t('campaignRequests.title') },
        { name: 'type', title: t('campaignRequests.type') },
        { name: MAIN_CR_FIELD, title: t('campaignRequests.mainCR') },
        { name: SUB_CR_PATH, title: t('campaignRequests.subCR') },
        { name: 'campaign', title: t('campaignRequests.campaign') },
        // TODO: Uncomment when the ZASM issue CART-18763 is solved
        /* { name: OVERVIEW_COLUMNS.BLOCKING_LOCATIONS, title: t('campaignRequests.blockingLocation') },
        { name: OVERVIEW_COLUMNS.ESTIMATED_CARS, title: t('campaignRequests.estimatedVehicleVolume') },
        { name: OVERVIEW_COLUMNS.VALIDATED_CARS, title: t('campaignRequests.validatedAmountOfVehicles') },
        { name: OVERVIEW_COLUMNS.NO_REWORK, title: t('campaignRequests.noRework') },
        { name: OVERVIEW_COLUMNS.REWORK_NOT_STARTED, title: t('campaignRequests.reworkNotStarted') },
        { name: OVERVIEW_COLUMNS.REWORK_OPEN, title: t('campaignRequests.reworkOpen') },
        { name: OVERVIEW_COLUMNS.REWORK_FINISHED, title: t('campaignRequests.reworkFinished') },
        { name: OVERVIEW_COLUMNS.OK_WITHOUT_REWORK, title: t('campaignRequests.okWithoutRework') },
        { name: OVERVIEW_COLUMNS.REWORK_IN_FIELD, title: t('campaignRequests.reworkInField') }, */
        { name: 'affectedPlants', title: t('hardware.affectedPlants') },
        {
          name: 'classification',
          title: t('classification.classification'),
          getCellValue: (row: any, columnName: string) => row[columnName]
            ?.map((value) => translateCellWithFilterValues(value, FilterKeysToTranslate.CLASSIFICATION, formatterData?.lang, formatterData?.options))
            .join(', ') || '',
        },
        { name: 'affectedModelSeries', title: t('affectedVehicles.affectedModelSeries') },
        {
          name: 'triggerSource',
          title: t('summary.triggerSource'),
          getCellValue: (row: any, columnName: string) =>
            translateCellWithFilterValues(row[columnName], FilterKeysToTranslate.SOURCE, formatterData?.lang, formatterData?.options),
        },
        {
          name: 'affectsChina',
          title: t('classification.GB18285_Relevance'),
          getCellValue: (row: any, columnName: string) =>
            translateCellWithFilterValues(row[columnName], FilterKeysToTranslate.AFFECTS_CHINA, formatterData?.lang, formatterData?.options),
        },
        {
          name: 'blockingRecommendation',
          title: t('classification.blockingRecommendation'),
          getCellValue: (row: any, columnName: string) =>
            translateCellWithFilterValues(row[columnName]?.toUpperCase(), FilterKeysToTranslate.BLOCKING_RECOMMENDATION,
              formatterData?.lang, formatterData?.options),
        },
        {
          name: 'customerRelevance',
          title: t('classification.customerRelevancy'),
          getCellValue: (row: any, columnName: string) =>
            translateCellWithFilterValues(row[columnName], FilterKeysToTranslate.CUSTOMER_RELEVANCY, formatterData?.lang, formatterData?.options),
        },
        { name: 'locationInternal', title: t('involvedParties.causerInternalExternal') },
        { name: 'mbosRelevance', title: t('software.MBOSOtaRevelancy') },
        { name: DESCRIPTION.POSSIBLE_CAUSER, title: t('involvedParties.possibleCauser') },
        { name: 'supplierNumber', title: t('dialog.supplierNumber') },
        { name: 'supplierName', title: t('dialog.supplierName') },
        { name: DESCRIPTION.TIER1_SUPPLIER.NAME, title: t('dialog.tier1SupplierName') },
        { name: DESCRIPTION.TIER1_SUPPLIER.NUMBER, title: t('dialog.tier1SupplierNumber') },
        { name: DESCRIPTION.CONTACT_PERSON_MP, title: t('dialog.contactPersonMP') },
        { name: DESCRIPTION.IRA_NUMBER, title: t('involvedParties.iraNumber') },
        { name: 'partNumbers', title: t('hardware.numberNew') },
        { name: 'status', title: t('campaignRequests.status') },
        { name: 'version', title: t('campaignRequests.version') },
        { name: 'versionDate', title: t('campaignRequests.date') },
        { name: DESCRIPTION.CONTACT_PERSON, title: t('campaignRequests.contactPerson') },
        { name: 'department', title: t('campaignRequests.departament') },
        { name: 'asignedUser', title: t('campaignRequests.assignedUser') },
        { name: 'created', title: t('campaignRequests.created') },
      ],
      columnsNotTree: [
        { name: 'title', title: t('campaignRequests.title') },
        { name: 'type', title: t('campaignRequests.type') },
        { name: MAIN_CR_FIELD, title: t('campaignRequests.mainCR') },
        // TODO: Uncomment when the ZASM issue CART-18763 is solved
        /* { name: OVERVIEW_COLUMNS.BLOCKING_LOCATIONS, title: t('campaignRequests.blockingLocation') },
        { name: OVERVIEW_COLUMNS.ESTIMATED_CARS, title: t('campaignRequests.estimatedVehicleVolume') },
        { name: OVERVIEW_COLUMNS.NO_REWORK, title: t('campaignRequests.noRework') },
        { name: OVERVIEW_COLUMNS.REWORK_NOT_STARTED, title: t('campaignRequests.reworkNotStarted') },
        { name: OVERVIEW_COLUMNS.REWORK_OPEN, title: t('campaignRequests.reworkOpen') },
        { name: OVERVIEW_COLUMNS.REWORK_FINISHED, title: t('campaignRequests.reworkFinished') },
        { name: OVERVIEW_COLUMNS.OK_WITHOUT_REWORK, title: t('campaignRequests.okWithoutRework') },
        { name: OVERVIEW_COLUMNS.REWORK_IN_FIELD, title: t('campaignRequests.reworkInField') }, */
        { name: 'affectedPlants', title: t('hardware.affectedPlants') },
        {
          name: 'classification',
          title: t('classification.classification'),
          getCellValue: (row: any, columnName: string) => row[columnName]
            ?.map((value) => translateCellWithFilterValues(value, FilterKeysToTranslate.CLASSIFICATION, formatterData?.lang, formatterData?.options))
            .join(', ') || '',
        },
        { name: 'affectedModelSeries', title: t('affectedVehicles.affectedModelSeries') },
        {
          name: 'triggerSource',
          title: t('summary.triggerSource'),
          getCellValue: (row: any, columnName: string) =>
            translateCellWithFilterValues(row[columnName], FilterKeysToTranslate.SOURCE, formatterData?.lang, formatterData?.options),
        },
        {
          name: 'affectsChina',
          title: t('classification.GB18285_Relevance'),
          getCellValue: (row: any, columnName: string) =>
            translateCellWithFilterValues(row[columnName], FilterKeysToTranslate.AFFECTS_CHINA, formatterData?.lang, formatterData?.options),
        },
        {
          name: 'blockingRecommendation',
          title: t('classification.blockingRecommendation'),
          getCellValue: (row: any, columnName: string) =>
            translateCellWithFilterValues(row[columnName]?.toUpperCase(), FilterKeysToTranslate.BLOCKING_RECOMMENDATION,
              formatterData?.lang, formatterData?.options),
        },
        {
          name: 'customerRelevance',
          title: t('classification.customerRelevancy'),
          getCellValue: (row: any, columnName: string) =>
            translateCellWithFilterValues(row[columnName], FilterKeysToTranslate.CUSTOMER_RELEVANCY, formatterData?.lang, formatterData?.options),
        },
        { name: 'locationInternal', title: t('involvedParties.causerInternalExternal') },
        { name: 'mbosRelevance', title: t('software.MBOSOtaRevelancy') },
        { name: DESCRIPTION.POSSIBLE_CAUSER, title: t('involvedParties.possibleCauser') },
        { name: 'supplierNumber', title: t('dialog.supplierNumber') },
        { name: 'supplierName', title: t('dialog.supplierName') },
        { name: DESCRIPTION.TIER1_SUPPLIER.NAME, title: t('dialog.tier1SupplierName') },
        { name: DESCRIPTION.TIER1_SUPPLIER.NUMBER, title: t('dialog.tier1SupplierNumber') },
        { name: DESCRIPTION.CONTACT_PERSON_MP, title: t('dialog.contactPersonMP') },
        { name: DESCRIPTION.IRA_NUMBER, title: t('involvedParties.iraNumber') },
        { name: 'partNumbers', title: t('hardware.numberNew') },
        { name: 'status', title: t('campaignRequests.status') },
        { name: 'version', title: t('campaignRequests.version') },
        { name: 'versionDate', title: t('campaignRequests.date') },
        { name: DESCRIPTION.CONTACT_PERSON, title: t('campaignRequests.contactPerson') },
        { name: 'department', title: t('campaignRequests.departament') },
        { name: 'asignedUser', title: t('campaignRequests.assignedUser') },
        { name: 'created', title: t('campaignRequests.created') },
      ],
      extensions: [
        { columnName: 'title', width: 250, extraWidthTooptip: 50, visible: true },
        { columnName: 'type', width: 100, align: 'center', visible: true },
        { columnName: MAIN_CR_FIELD, width: 240, visible: true },
        { columnName: SUB_CR_PATH, width: 240, visible: true },
        { columnName: 'campaign', width: 250, visible: true },
        // TODO: Uncomment when the ZASM issue CART-18763 is solved
        /* { columnName: OVERVIEW_COLUMNS.BLOCKING_LOCATIONS, width: 150, visible: true },
        { columnName: OVERVIEW_COLUMNS.ESTIMATED_CARS, width: 150, align: 'center', visible: true },
        { columnName: OVERVIEW_COLUMNS.VALIDATED_CARS, width: 150, align: 'center', visible: true },
        { columnName: OVERVIEW_COLUMNS.NO_REWORK, width: 130, align: 'center', visible: true },
        { columnName: OVERVIEW_COLUMNS.REWORK_NOT_STARTED, width: 150, align: 'center', visible: true },
        { columnName: OVERVIEW_COLUMNS.REWORK_OPEN, width: 130, align: 'center', visible: true },
        { columnName: OVERVIEW_COLUMNS.REWORK_FINISHED, width: 150, align: 'center', visible: true },
        { columnName: OVERVIEW_COLUMNS.OK_WITHOUT_REWORK, width: 150, align: 'center', visible: true },
        { columnName: OVERVIEW_COLUMNS.REWORK_IN_FIELD, width: 130, align: 'center', visible: true }, */
        { columnName: 'affectedPlants', extraWidthTooptip: 50, width: 150, visible: false },
        { columnName: 'classification', extraWidthTooptip: 50, width: 130, visible: false },
        { columnName: 'affectedModelSeries', extraWidthTooptip: 50, width: 170, visible: false },
        { columnName: 'triggerSource', extraWidthTooptip: 50, width: 130, visible: false },
        { columnName: 'affectsChina', extraWidthTooptip: 50, width: 160, visible: false },
        { columnName: 'blockingRecommendation', extraWidthTooptip: 50, width: 200, visible: false },
        { columnName: 'customerRelevance', extraWidthTooptip: 50, width: 170, visible: false },
        { columnName: 'locationInternal', extraWidthTooptip: 50, width: 230, visible: false },
        { columnName: 'mbosRelevance', extraWidthTooptip: 50, width: 200, visible: false },
        { columnName: DESCRIPTION.POSSIBLE_CAUSER, extraWidthTooptip: 50, width: 180, visible: false },
        { columnName: 'supplierNumber', extraWidthTooptip: 50, width: 150, visible: false },
        { columnName: 'supplierName', extraWidthTooptip: 50, width: 150, visible: false },
        { columnName: DESCRIPTION.TIER1_SUPPLIER.NAME, extraWidthTooptip: 50, width: 150, visible: false },
        { columnName: DESCRIPTION.TIER1_SUPPLIER.NUMBER, extraWidthTooptip: 50, width: 150, visible: false },
        { columnName: DESCRIPTION.CONTACT_PERSON_MP, extraWidthTooptip: 50, width: 150, visible: false },
        { columnName: DESCRIPTION.IRA_NUMBER, extraWidthTooptip: 50, width: 130, visible: false },
        { columnName: 'partNumbers', extraWidthTooptip: 50, width: 120, visible: false },
        { columnName: 'status', width: 240, visible: true },
        { columnName: 'version', width: 85, visible: true },
        { columnName: 'versionDate', width: 250, align: 'center', visible: true },
        { columnName: DESCRIPTION.CONTACT_PERSON, width: 250, visible: true },
        { columnName: 'department', width: 120, visible: true },
        { columnName: 'asignedUser', width: 250, visible: true },
        { columnName: 'created', width: 250, align: 'center', visible: true },
      ],
      columnResizingExtensions: [
        { columnName: 'title', minWidth: 125, maxWidth: 500 },
        { columnName: 'type', minWidth: 100, maxWidth: 200 },
        { columnName: MAIN_CR_FIELD, minWidth: 145, maxWidth: 400 },
        { columnName: SUB_CR_PATH, minWidth: 120, maxWidth: 400 },
        { columnName: 'campaign', minWidth: 120, maxWidth: 400 },
        { columnName: 'status', minWidth: 190, maxWidth: 400 },
        // TODO: Uncomment when the ZASM issue CART-18763 is solved
        /* { columnName: OVERVIEW_COLUMNS.BLOCKING_LOCATIONS, minWidth: 100, maxWidth: 400 },
        { columnName: OVERVIEW_COLUMNS.ESTIMATED_CARS, minWidth: 120, maxWidth: 400 },
        { columnName: OVERVIEW_COLUMNS.VALIDATED_CARS, minWidth: 120, maxWidth: 400 },
        { columnName: OVERVIEW_COLUMNS.NO_REWORK, minWidth: 80, maxWidth: 400 },
        { columnName: OVERVIEW_COLUMNS.REWORK_NOT_STARTED, minWidth: 80, maxWidth: 400 },
        { columnName: OVERVIEW_COLUMNS.REWORK_OPEN, minWidth: 80, maxWidth: 400 },
        { columnName: OVERVIEW_COLUMNS.REWORK_FINISHED, minWidth: 80, maxWidth: 400 },
        { columnName: OVERVIEW_COLUMNS.OK_WITHOUT_REWORK, minWidth: 80, maxWidth: 400 },
        { columnName: OVERVIEW_COLUMNS.REWORK_IN_FIELD, minWidth: 80, maxWidth: 400 }, */
        { columnName: 'affectedPlants', minWidth: 100, maxWidth: 400 },
        { columnName: 'classification', minWidth: 100, maxWidth: 400 },
        { columnName: 'affectedModelSeries', minWidth: 100, maxWidth: 400 },
        { columnName: 'triggerSource', minWidth: 100, maxWidth: 400 },
        { columnName: 'affectsChina', minWidth: 100, maxWidth: 400 },
        { columnName: 'blockingRecommendation', minWidth: 100, maxWidth: 400 },
        { columnName: 'customerRelevance', minWidth: 100, maxWidth: 400 },
        { columnName: 'locationInternal', minWidth: 100, maxWidth: 400 },
        { columnName: 'mbosRelevance', minWidth: 100, maxWidth: 400 },
        { columnName: DESCRIPTION.POSSIBLE_CAUSER, minWidth: 100, maxWidth: 400 },
        { columnName: 'supplierNumber', minWidth: 100, maxWidth: 400 },
        { columnName: 'supplierName', minWidth: 100, maxWidth: 400 },
        { columnName: DESCRIPTION.TIER1_SUPPLIER.NAME, minWidth: 100, maxWidth: 400 },
        { columnName: DESCRIPTION.TIER1_SUPPLIER.NUMBER, minWidth: 100, maxWidth: 400 },
        { columnName: DESCRIPTION.CONTACT_PERSON_MP, minWidth: 100, maxWidth: 400 },
        { columnName: DESCRIPTION.IRA_NUMBER, minWidth: 100, maxWidth: 400 },
        { columnName: 'partNumbers', minWidth: 100, maxWidth: 400 },
        { columnName: 'version', minWidth: 80, maxWidth: 400 },
        { columnName: 'versionDate', minWidth: 250, maxWidth: 400 },
        { columnName: DESCRIPTION.CONTACT_PERSON, minWidth: 140, maxWidth: 400 },
        { columnName: 'department', minWidth: 120, maxWidth: 200 },
        { columnName: 'asignedUser', minWidth: 125, maxWidth: 400 },
        { columnName: 'created', minWidth: 250, maxWidth: 400 },
      ],
    },
    subCampaignOverview: {
      columns: [
        { name: 'title', title: t('campaignRequests.title') },
        { name: SUB_CR_PATH, title: t('campaignRequests.subCR') },
        { name: 'status', title: t('campaignRequests.status') },
        { name: 'version', title: t('campaignRequests.version') },
        { name: 'versionDate', title: t('campaignRequests.date') },
        { name: DESCRIPTION.CONTACT_PERSON, title: t('campaignRequests.contactPerson') },
        { name: 'department', title: t('campaignRequests.departament') },
        { name: 'asignedUser', title: t('campaignRequests.assignedUser') },
        { name: 'creationDate', title: t('campaignRequests.created') },
      ],
      extensions: [
        { columnName: 'title', width: 220 },
        { columnName: SUB_CR_PATH, width: 250 },
        { columnName: 'status', width: 150 },
        { columnName: 'version', width: 130 },
        { columnName: 'versionDate', width: 350 },
        { columnName: DESCRIPTION.CONTACT_PERSON, width: 250 },
        { columnName: 'department', width: 200 },
        { columnName: 'asignedUser', width: 250 },
        { columnName: 'creationDate', width: 350 },
      ],
    },
  };

  if (isCampaignOverview) {
    return info.campaignOverview;
  }

  if (isSubCROverview) {
    return info.subCampaignOverview;
  }

  return isSupplier ? info.suppliers : info.users;
};

export const defaultFilter = (isSupplier: boolean, defaultValue?: string, isCampaignOverview?: boolean, isSubCROverview?: boolean) => {
  if (isSupplier) {
    return {
      supplierId: defaultValue || '',
      supplierName: '',
    };
  }

  if (isCampaignOverview) {
    return {
      mainCR: '',
      subCR: '',
      version: '',
      contactPerson: '',
      department: '',
      status: '',
      asignedUser: '',
      title: '',
      versionDate: '',
      created: '',
    };
  }

  if (isSubCROverview) {
    return {
      subCR: '',
      version: '',
      contactPerson: '',
      department: '',
      status: '',
      asignedUser: '',
      title: '',
      versionDate: '',
      creationDate: '',
    };
  }

  return {
    lastName: '',
    firstName: '',
    department: '',
    userId: defaultValue || '',
  };
};

export const defaultFilterValues = (isSupplier: boolean, defaultValue: string, isCampaignOverview: boolean, isSubCROverview: boolean = false) => {
  if (isSupplier) return [{ columnName: 'supplierId', value: defaultValue, operation: 'contains' }];

  if (isCampaignOverview || isSubCROverview) return [{ columnName: MAIN_CR_FIELD, value: '', operation: 'contains' }];

  return [{ columnName: 'userCode', value: defaultValue, operation: 'contains' }];
};

export const defaultTableInfo = (isSupplier: boolean, isCampaignOverview: boolean, isSubCROverview: boolean,
  defaultValue: string, t: TFunction, pageSize?: number, formatterData?: FormatterData<TableTranslations | undefined>) => {
  const tableColumnsData = tableColumns(t, isSupplier, isCampaignOverview, isSubCROverview, formatterData) as unknown as TableColumnsData;

  return {
    columns: tableColumnsData.columns,
    columnsNotTree: tableColumnsData?.columnsNotTree,
    columnExtensions: tableColumnsData.extensions,
    columnResizingExtensions: tableColumnsData.columnResizingExtensions,
    data: [],
    rows: [],
    filters: defaultFilter(isSupplier, defaultValue, isCampaignOverview, isSubCROverview),
    selection: [],
    totalCount: 0,
    pageSize,
    pageSizes: [10, 20, 50, 100],
    totalPages: 0,
    currentPage: 0,
  };
};

export const tableMessages = (t: TFunction, currentPage: number, totalPages: number, loading: boolean) => ({
  table: {
    noData: loading ? '' : t('noResults'),
  },
  filter: {
    filterPlaceholder: t('dialog.filter'),
  },
  paging: {
    rowsPerPage: t('dialog.rowsPerPage'),
    ...(totalPages
      ? { info: t('dialog.page', { page: currentPage + 1, total: Math.floor(totalPages) }) }
      : {}
    ),
  },
});

export const mapCampaignManagersRows = (result) => result.usersResponseFindAll.map((row) => ({
  lastName: row.lastName,
  firstName: row.firstName,
  department: row.department,
  userCode: row.userCode,
}));

// This gets the table selection, if is the first time and we have defaultselection, set defaultselection,
// if dont have current selection and have defaultSelection, we set defaultselection
export const getSelectionTableInfo = (firstRender, defaultSelection, selection) => (
  ((firstRender && defaultSelection) || (selection?.length === 0 && defaultSelection)) ? [defaultSelection] : selection
);

export const getTitle = (title, lang) => {
  const titleDE = title.DE;
  const titleEN = title.EN;

  if (lang === GERMAN) return (titleDE && titleDE !== DOORMAN_PREFIX_DE) ? titleDE : titleEN;

  if (lang === ENGLISH) return (titleEN && titleEN !== DOORMAN_PREFIX_EN) ? titleEN : titleDE;
};

export const mapSubCampaignRows = (allResults, lang, timeZone, t, arrowComponent, isSubCRChildren = false) => allResults?.map((result) => {
  const CAMPAIGN_COUNT_FIELDS = [
    OVERVIEW_COLUMNS.NO_REWORK,
    OVERVIEW_COLUMNS.REWORK_OPEN,
    OVERVIEW_COLUMNS.REWORK_FINISHED,
    OVERVIEW_COLUMNS.OK_WITHOUT_REWORK,
    OVERVIEW_COLUMNS.REWORK_IN_FIELD,
  ];
  const SUB_CR_COUNT_FIELDS = [
    ...CAMPAIGN_COUNT_FIELDS,
    OVERVIEW_COLUMNS.REWORK_NOT_STARTED,
  ];
  const res = {
    ...result,
    title: getTitle(result.title, lang),
    subCR: result.id,
    version: result.version,
    validatedAmountOfVehicles: result.vehiclesTotal,
    versionDate: getFullFormattedDate(result.versionDate, timeZone, LONG_FULL_DATE_FORMAT),
    [VERSION_DATE_RAW]: result.versionDate,
    contactPerson: result.contactPerson.summary,
    department: result.creationUser.department,
    statusCode: result.status,
    status: t(`${'subCampaign'}.${result.status}`, { plantNumber: result?.plant }),
    asignedUser: result.assignedUser.summary,
    [isSubCRChildren ? 'created' : 'creationDate']: getFullFormattedDate(result.creationDate, timeZone, LONG_FULL_DATE_FORMAT),
    [isSubCRChildren ? CREATED_RAW : CREATION_DATE_RAW]: result.creationDate,
    approvedByCampaignManager: result?.approvedByCampaignManager,
    isSubCRChildren,
    // TODO: Uncomment when the ZASM issue CART-18763 is solved
    // ...parseObjectNegativeValuesToBlank(SUB_CR_COUNT_FIELDS, result),
  };

  if (result?.campaigns && result.campaigns?.length > 0) {
    res.childRows = result.campaigns.map((campaign) => {
      const campaignRow = {
        id: campaign.campaignNumber,
        campaign: campaign.campaignNumber,
        status: campaign.campaignStatus ? t(`intaktStatus.${campaign.campaignStatus}`) : '',
        thirdLevelRow: true,
        hiddenSubCR: result.id,
        // TODO: Uncomment when the ZASM issue CART-18763 is solved
        /* validatedAmountOfVehicles: campaign.vehiclesTotal,
        ...parseObjectNegativeValuesToBlank(CAMPAIGN_COUNT_FIELDS, campaign), */
        childRows: undefined,
      };

      // TODO: Uncomment when the ZASM issue CART-18763 is solved
      /* if (campaign.blockingLocations?.length) {
        campaignRow.childRows = campaign.blockingLocations.map((blockingLocation, index) => ({
          id: `${campaign.campaignNumber}_blockingLocation_${index}`,
          blockingLocations: blockingLocation.location,
          validatedAmountOfVehicles: blockingLocation.vehiclesTotal,
          ...parseObjectNegativeValuesToBlank(CAMPAIGN_COUNT_FIELDS, blockingLocation),
          hiddenCampaign: campaign.campaignNumber,
          hiddenSubCR: result.id,
          fourthLevelRow: true,
        }));
      } */

      return campaignRow;
    });
  }

  return res;
});

export const mapCampaingsRows = (allResults, lang, timeZone, t, arrowComponent) => allResults.map((result) => {
  const res = {
    ...result,
    title: getTitle(result.title, lang),
    type: result.type,
    mainCR: result.id,
    version: result.version,
    estimatedAmountOfVehicles: result.estimatedVehicleVolume,
    versionDate: getFullFormattedDate(result.versionDate, timeZone, LONG_FULL_DATE_FORMAT),
    [VERSION_DATE_RAW]: result.versionDate,
    contactPerson: result.contactPerson.summary,
    department: result.creationUser.department,
    affectedPlants: result.affectedPlants?.map((plant) => plant.plantName).join(', ') ?? undefined,
    classification: result.potentiallySCECertificationRelevant,
    affectedModelSeries: getAffectedModelSeries(result.affectedPlants),
    statusCode: result.status,
    status: t(`campaign.${result.status}`),
    locationInternal: getLocationInternalTranslation(result.locationInternal, lang),
    supplierNumber: result?.supplierNumber,
    supplierName: result?.supplierName,
    [DESCRIPTION.TIER1_SUPPLIER.NUMBER]: result?.[DESCRIPTION.TIER1_SUPPLIER.NUMBER],
    [DESCRIPTION.TIER1_SUPPLIER.NAME]: result?.[DESCRIPTION.TIER1_SUPPLIER.NAME],
    [DESCRIPTION.CONTACT_PERSON_MP]: result?.[DESCRIPTION.CONTACT_PERSON_MP],
    asignedUser: result.assignedUser.summary,
    partNumbers: result.partNumbers?.join(', ') ?? undefined,
    created: getFullFormattedDate(result.creationDate, timeZone, LONG_FULL_DATE_FORMAT),
    [CREATED_RAW]: result.creationDate,
    // TODO: Uncomment when the ZASM issue is solved
    // ...parseObjectNegativeValuesToBlank(VEHICLE_STATUS_COMMON_COUNT_COLUMNS, result),
  };

  delete res.subCampaigns;

  if (result?.subCampaigns && result.subCampaigns?.length > 0) {
    res.childRows = mapSubCampaignRows(result.subCampaigns, lang, timeZone, t, arrowComponent, true);
  }

  return res;
});

export const haveChildrensInThisPage = (rows, property) => rows.some((row) => row?.[property]);

export const defaultDisabledColumnsForVisibility = [
  { columnName: MAIN_CR_FIELD, togglingEnabled: false },
  { columnName: SUB_CR_PATH, togglingEnabled: false },
  { columnName: 'campaign', togglingEnabled: false },
];

export const columnSettingsToDefaultWidthColumns = (columns: ColumnExtensionOverview[] = []) => columns.map((column) => ({
  columnName: column.columnName,
  width: column.width,
}));

export const columnSettingsToDefaultVisibleColumns = (columns: ColumnExtensionOverview[] = []): VisibilityColumnsOverview[] =>
  columns.map((column) => ({
    columnName: column.columnName,
    visible: column.visible !== false,
  }));

const translateCellWithFilterValues = (value: string, type: FilterKeysToTranslate, lang: string = GERMAN,
  optionsToTranslate?: TableTranslations): string => {
  if (value && optionsToTranslate) {
    return (optionsToTranslate?.[type]?.options?.[value]?.[lang.toUpperCase()] ?? value);
  }

  return value;
};

export const buttonTriggerLateralFilter = ['affectedPlants', 'classification', 'affectedModelSeries', DESCRIPTION.IRA_NUMBER,
  'triggerSource', 'affectsChina', 'blockingRecommendation', 'customerRelevance', 'locationInternal', 'mbosRelevance',
  DESCRIPTION.POSSIBLE_CAUSER, 'supplierNumber', 'supplierName', DESCRIPTION.TIER1_SUPPLIER.NAME, DESCRIPTION.TIER1_SUPPLIER.NUMBER,
  DESCRIPTION.CONTACT_PERSON_MP, 'partNumbers', 'campaign', 'type',
];

export const findFilterKey = (sort: any, isExcel: boolean = false) => { // TODO review sorting is string[] this will fail item.key === sort
  if (isExcel && sort.includes(SUB_CR_PATH)) {
    return opensearchFieldMapping.created;
  }

  const filterHeader = HEADER_TO_FILTER[sort] || sort;

  return combinedMenus.find((item) => item.key === filterHeader)?.value || opensearchFieldMapping.created;
};

export const getTimeToDebounce = (isSubCROrCROverview: boolean, isfirstLoad: boolean, isUserTyping = false): number => {
  if (isUserTyping) return 1000;
  if (isSubCROrCROverview && isfirstLoad) return 0;
  else if (isSubCROrCROverview) return 500;
  else return 500;
};
